import { useEffect, useState } from 'react';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@clay-run/glaze/dropdown/index';
import { Icon } from '@clay-run/glaze/icon';
import { Text } from '@clay-run/glaze/text';

import { useEnrollQueue } from '../../EnrollQueue/EnrollQueueContext';
import { useFlowRequests } from '../../EnrollQueue/requests-hooks';
import { RequestStatusBadge, extractResponseCode, formatRelativeTime } from '../../EnrollQueue/request-format';
import type { RequestScope } from '@shared/flow/requests';
import { FLOW_ICONS } from '../../icons';
import { EmptyState } from '../ui/EmptyState';
import { RailRow } from './RailRow';
import { RAIL_PANE_GAP, RailSearchRow } from './RailSearchRow';

export interface ActionsIndexProps {
  /**
   * Pushes a value IN to the search box / contactId filter — never read back
   * out. A fresh object per "View all" click elsewhere in the app (e.g. a
   * contact's Recent Actions section) — see the seed effect below, which
   * keys off object identity so the same contact seeded twice in a row still
   * re-fires. This surface does not own the seed's lifetime; whoever renders
   * `ActionsIndex` (U14's `RailDock`) is the one reading it off context and
   * handing it down as a prop.
   */
  requestsSearchSeed?: { value: string; contactId?: string };
}

/**
 * The V2 rail's Actions tab index (U11, dispatch plan 2026-10-01, following
 * the `sc-terra-flow` prototype's `rail/actions/ActionsIndex.tsx`) — a search
 * box + a scope dropdown above a collapsed list of fired instant
 * actions and submitted workflow enrollments. Reads the real
 * `useFlowRequests` feed (byte-identical query shape to V1's `RequestsTab`)
 * and renders each row as a `RailRow` rather than `RequestsTab`'s hand-rolled
 * button markup.
 *
 * This component does NOT render the request detail itself — `RailDock`
 * (U14) owns dispatching between this list and `ActionsDetail` (U12) based on
 * `selectedRequestId`; this component only ever calls `openRequestDetail(id)`
 * and lets the dock decide what happens next.
 */
export function ActionsIndex({ requestsSearchSeed }: ActionsIndexProps) {
  const { openRequestDetail } = useEnrollQueue();
  const [search, setSearch] = useState(requestsSearchSeed?.value ?? '');
  const [scope, setScope] = useState<RequestScope>('mine');
  const [contactIdFilter, setContactIdFilter] = useState<string | null>(requestsSearchSeed?.contactId ?? null);
  // `DebouncedSearchInput` is uncontrolled (`initialValue` only) — remounting
  // it via this key is what lets a NEW seed actually move the visible text;
  // without it, the input would keep showing whatever the rep had typed
  // before the seed arrived.
  const [seedGen, setSeedGen] = useState(0);

  useEffect(() => {
    if (requestsSearchSeed) {
      setSearch(requestsSearchSeed.value);
      setContactIdFilter(requestsSearchSeed.contactId ?? null);
      setSeedGen(g => g + 1);
    }
  }, [requestsSearchSeed]);

  const { data, isLoading, isError } = useFlowRequests(search, contactIdFilter ?? undefined, scope);
  const entries = data ?? [];

  return (
    <div className={`flex flex-col ${RAIL_PANE_GAP}`}>
      <RailSearchRow
        inputKey={seedGen}
        initialValue={search}
        aria-label="Search requests by contact"
        placeholder="Search by contact…"
        onSearch={value => {
          // Guards against the remount-echo above: `DebouncedSearchInput`
          // fires `onSearch` from its own effects when it mounts/remounts
          // with a new `initialValue`, which would otherwise clear a
          // just-seeded `contactIdFilter` the instant the seed itself landed
          // — before the rep ever touched the box by hand.
          if (value === search) return;
          setSearch(value);
          setContactIdFilter(null);
        }}
        trailing={
          <Dropdown placement="bottom-end">
            <DropdownButton size="sm" variant="secondary" aria-label="Request scope">
              {scope === 'all' ? 'Everyone' : 'Mine'}
              <Icon type={FLOW_ICONS.dropdown} size="sm" aria-hidden />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem onClick={() => setScope('mine')}>Mine</DropdownItem>
              <DropdownItem onClick={() => setScope('all')}>Everyone</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        }
      />
      {isLoading ? (
        <Text size="xs" variant="tertiary" className="py-8 text-center">
          Loading…
        </Text>
      ) : isError ? (
        <Text size="xs" variant="critical" className="py-8 text-center">
          Failed to load requests.
        </Text>
      ) : entries.length === 0 ? (
        <EmptyState
          title="No requests"
          description={
            search.trim()
              ? `No runs match "${search.trim()}".`
              : scope === 'all'
                ? 'No actions yet.'
                : 'Fire an action or enroll a contact to see it here.'
          }
        />
      ) : (
        <div className="flex flex-col">
          {entries.map(entry => {
            const who = entry.contact?.name || entry.contact?.email || entry.contact?.id || null;
            return (
              <RailRow
                variant="flat"
                key={entry.id}
                aria-label={`Open request: ${who ?? entry.actionName}`}
                onToggle={() => openRequestDetail(entry.id)}
                icon={entry.kind === 'workflow' ? FLOW_ICONS.send : FLOW_ICONS.actions}
                title={who ?? entry.actionName}
                meta={who ? <span className="truncate">{entry.actionName}</span> : undefined}
                trailing={
                  <>
                    <RequestStatusBadge status={entry.status} code={extractResponseCode(entry.response)} />
                    <Text size="xs" variant="tertiary" className="whitespace-nowrap">
                      {formatRelativeTime(entry.ts)}
                    </Text>
                  </>
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
